import type { IRewardsService } from '../../../services/rewards/IRewardsService';

type RuleMessage = { text: string; tiers?: string[] };

export type HostMessagesState = {
  messages: string[];
  currentIndex: number;
  visible: boolean;
};

export type HostMessagesListener = (state: HostMessagesState) => void;

const ROTATE_INTERVAL_MS = 30_000;
const FADE_DURATION_MS = 400;

export class HostMessagesManager {
  private allMessages: RuleMessage[] = [];
  private machineTierId: string | null = null;
  private state: HostMessagesState = { messages: [], currentIndex: 0, visible: true };
  private listeners: HostMessagesListener[] = [];
  private rotateTimer: ReturnType<typeof setInterval> | null = null;
  private fadeTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private requestGeneration = 0;

  constructor(private readonly rewardsService: IRewardsService) {}

  async init(): Promise<void> {
    await this.refresh();
  }

  setMachineTierId(tierId: string | null): void {
    if (tierId === this.machineTierId) return;
    this.machineTierId = tierId;
    this.rebuildFiltered();
  }

  async refresh(): Promise<void> {
    const generation = ++this.requestGeneration;
    try {
      const raw = await this.rewardsService.getHostMessages();
      if (this.destroyed) return;
      if (generation !== this.requestGeneration) return;
      this.allMessages = raw;
      this.stopTimers();
      this.rebuildFiltered();
    } catch {
      // Non-critical — no messages to show
    }
  }

  private rebuildFiltered(): void {
    const tier = this.machineTierId;
    const filtered = this.allMessages
      .filter((m) => !m.tiers || m.tiers.length === 0 || (tier != null && m.tiers.includes(tier)))
      .map((m) => m.text);
    this.updateState({ messages: filtered, currentIndex: 0, visible: true });
    this.startRotation();
  }

  destroy(): void {
    this.destroyed = true;
    this.stopTimers();
    this.listeners = [];
  }

  private stopTimers(): void {
    if (this.rotateTimer) {
      clearInterval(this.rotateTimer);
      this.rotateTimer = null;
    }
    if (this.fadeTimeout) {
      clearTimeout(this.fadeTimeout);
      this.fadeTimeout = null;
    }
  }

  getState(): HostMessagesState {
    return this.state;
  }

  subscribe(listener: HostMessagesListener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  private startRotation(): void {
    this.stopTimers();
    if (this.state.messages.length <= 1) return;

    this.rotateTimer = setInterval(() => {
      this.updateState({ visible: false });
      this.fadeTimeout = setTimeout(() => {
        if (this.destroyed) return;
        const next = (this.state.currentIndex + 1) % this.state.messages.length;
        this.updateState({ currentIndex: next, visible: true });
      }, FADE_DURATION_MS);
    }, ROTATE_INTERVAL_MS);
  }

  private updateState(partial: Partial<HostMessagesState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}
